'use client';
import { useRouter } from '@bprogress/next';
import DesktopMakeAnOfferModal from '@features/makeAnOffer/components/DesktopMakeAnOfferModal';
import MobileMakeAnOfferModal from '@features/makeAnOffer/components/MobileMakeAnOfferModal';
import { NavigationBar } from '@features/makeAnOffer/components/NavigationBar';
import { getOfferStepList } from '@features/makeAnOffer/utils';
import type { ChatDocument } from '@models/chat';
import { Elements } from '@stripe/react-stripe-js';
import { useQuery } from '@tanstack/react-query';
import { getOffer } from '@util/firestore/offers/offers.service';
import { hasValidVariations } from '@util/firestore/products';
import { cn, isMobile } from '@util/index';
import { isPhoneAuthenticated } from '@util/phoneAuth';
import { useAuth } from 'context/AuthContext';
import { useStripeContext } from 'context/StripeContext';
import { useToastContext } from 'context/ToastContext';
import { isVehicleCategory, type ProductDocument } from 'models/product';
import { usePathname } from 'next/navigation';
import { useState } from 'react';

export default function ChatOfferButton({
  chat,
  product,
  isSupportChat,
  className,
}: {
  chat: ChatDocument;
  product?: ProductDocument | null;
  isSupportChat: boolean;
  className?: string;
}) {
  const { userDoc, user } = useAuth();
  const { showErrorToast } = useToastContext();
  const { stripePromise } = useStripeContext();
  const router = useRouter();
  const pathname = usePathname();

  const [showMakeOffer, setShowMakeOffer] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  const isSeller = userDoc?.uid === chat.seller_id;
  const isBuyer = userDoc?.uid === chat.buyer_id;

  const { data: offer } = useQuery({
    queryKey: ['offer', chat.offer_id],
    queryFn: () => getOffer(chat.offer_id!),
    enabled: !!chat.offer_id,
  });

  const isOfferConcluded =
    offer?.state === 2 ||
    offer?.state === 3 ||
    (offer?.state === 0 &&
      !!offer.time_remaining &&
      offer.time_remaining < Date.now());

  const canOffer =
    !!userDoc &&
    !!product &&
    !isSupportChat &&
    !product.out_of_stock &&
    !isVehicleCategory(product.category) &&
    !chat.order_id &&
    (!chat.offer_id || isOfferConcluded) &&
    (isSeller || isBuyer);

  if (!canOffer || !product) return null;

  const offerMode: 'make' | 'send' = isSeller ? 'send' : 'make';
  const { steps, totalSteps } = getOfferStepList(product, offerMode);

  const handleBuyerClick = () => {
    if (!userDoc || user?.isAnonymous) {
      router.push(`/login?redirect=${encodeURI(pathname ?? '')}`);
      return;
    }
    if (!isPhoneAuthenticated(user, userDoc)) {
      router.push('/verify-phone');
      return;
    }
    if (!hasValidVariations(product, null)) {
      showErrorToast('Please select all options');
      return;
    }
    setShowMakeOffer(true);
  };

  const navigationBar = isSeller ? null : (
    <NavigationBar stepCount={totalSteps} currentStep={activeStep} />
  );

  return (
    <>
      <button
        type="button"
        onClick={isSeller ? () => setShowMakeOffer(true) : handleBuyerClick}
        className={cn(
          'whitespace-nowrap rounded-full border border-zinc-300 px-8 py-4 text-[1.6rem] font-semibold text-brand-black transition-colors hover:bg-zinc-50',
          className
        )}
      >
        {isSeller ? 'Send offer' : 'Make offer'}
      </button>

      <Elements stripe={stripePromise}>
        {isMobile() ? (
          <MobileMakeAnOfferModal
            isOpen={showMakeOffer}
            dismiss={() => setShowMakeOffer(false)}
            product={product}
            selectedVariations={null}
            stepData={{ steps, totalSteps }}
            navigationBar={navigationBar}
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            chat={chat}
            mode={offerMode}
          />
        ) : (
          <DesktopMakeAnOfferModal
            isOpen={showMakeOffer}
            dismiss={() => setShowMakeOffer(false)}
            product={product}
            selectedVariations={null}
            stepData={{ steps, totalSteps }}
            navigationBar={navigationBar}
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            chat={chat}
            mode={offerMode}
          />
        )}
      </Elements>
    </>
  );
}
